import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { nonBlankValidator } from '@app/sharedModule/validators/validators';
import { resolveShopPluginCdn } from '@app/pages/checkout-app/utils';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';
import { PercentFormShape } from '../digital-upsell/digital-upsell.model';

export type OrderProtectionPricingType = 'fixed' | 'percent';
export type OrderProtectionSelectingMethod = 'toggle' | 'checkbox' | 'quick_checkout';
export type OrderProtectionRoutingMode = 'automatic' | 'custom_url';
export type OrderProtectionInfoType = 'tooltip' | 'modal';

// Default (unselected) + selected colors for a selector. Toggle and Checkbox each keep their own pair.
export interface OrderProtectionSelectorColorsFormShape {
  defaultColor: FormControl<string>;
  selectedColor: FormControl<string>;
}

export interface OrderProtectionSelectingMethodFormShape {
  type: FormControl<OrderProtectionSelectingMethod>;
  toggle: FormGroup<OrderProtectionSelectorColorsFormShape>;
  checkbox: FormGroup<OrderProtectionSelectorColorsFormShape>;
  // "Auto-selected": protection is pre-selected, the shopper can opt out (shared by toggle & checkbox).
  autoSelected: FormControl<boolean>;
}

export interface OrderProtectionContinueWithoutFormShape {
  content: FormControl<string>;
  textColor: FormControl<string>;
  hoverColor: FormControl<string>;
}

// The Quick-checkout-button config — the button replaces the merchant's own checkout CTA.
export interface OrderProtectionQuickCheckoutFormShape {
  buttonLabel: FormControl<string>;
  loaderLabel: FormControl<string>;
  routing: FormControl<OrderProtectionRoutingMode>;
  customUrl: FormControl<string>;
  icon: FormControl<string>;
  // Data URL of an uploaded icon, kept so the picker can show it beside the presets.
  customIcon: FormControl<string>;
  backgroundColor: FormControl<string>;
  textColor: FormControl<string>;
  hoverColor: FormControl<string>;
  iconColor: FormControl<string>;
  cornerRadius: FormControl<number>;
  continueWithout: FormGroup<OrderProtectionContinueWithoutFormShape>;
}

export interface OrderProtectionListItemFormShape {
  // CDN url of the chosen icon ('' = No icon), or a data URL for an uploaded one.
  icon: FormControl<string>;
  customIcon: FormControl<string>;
  // Bold benefit title + a supporting description line (Misen-style modal rows).
  title: FormControl<string>;
  description: FormControl<string>;
}

// The modal's three reorderable sections (Figma: each has a drag handle).
export type OrderProtectionModalSection = 'headline' | 'body' | 'list';
export const ORDER_PROTECTION_MODAL_SECTIONS: OrderProtectionModalSection[] = ['headline', 'body', 'list'];

export interface OrderProtectionInfoFormShape {
  type: FormControl<OrderProtectionInfoType>;
  tooltipContent: FormControl<string>;
  modalHeadline: FormControl<string>;
  modalBody: FormControl<string>;
  listItems: FormArray<FormGroup<OrderProtectionListItemFormShape>>;
  // Fine print at the bottom of the modal (rich text — supports Privacy/Terms links, Misen-style).
  footer: FormControl<string>;
  // Order of the modal's Headline / Body / List Item sections (reorderable via drag handles).
  sectionOrder: FormControl<OrderProtectionModalSection[]>;
}

// Card border. Off by default; the colour/width/radius fields only apply when enabled.
export interface OrderProtectionBorderFormShape {
  enabled: FormControl<boolean>;
  color: FormControl<string>;
  width: FormControl<number>;
  radius: FormControl<number>;
}

export interface OrderProtectionFormShape {
  // Settings tab
  pricingMethod: FormControl<OrderProtectionPricingType>;
  flatPrice: FormControl<number>;
  // percent.cartPercentage is the only field the Figma exposes; min/max stay for the backend variant ladder.
  percent: FormGroup<PercentFormShape>;
  // Appearance tab
  title: FormControl<string>;
  subtitle: FormControl<string>;
  subtitleColor: FormControl<string>;
  border: FormGroup<OrderProtectionBorderFormShape>;
  productImageBase64: FormControl<string>;
  selectingMethod: FormGroup<OrderProtectionSelectingMethodFormShape>;
  quickCheckout: FormGroup<OrderProtectionQuickCheckoutFormShape>;
  info: FormGroup<OrderProtectionInfoFormShape>;
  customCode: FormGroup<CustomCodeFormShape>;
}

export const ORDER_PROTECTION_STARTER_TEMPLATE = `<div
  style="padding: 14px 20px;"
>
  <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
    <div style="font-weight:700;">{{title}}</div>
    <div data-pdq-slot="price" style="font-weight:600;"></div>
  </div>
  <!-- The Secure Checkout+ control — click handling + Shopify are ours. Keep this slot. -->
  <div data-pdq-slot="control"></div>
</div>`;

export const ORDER_PROTECTION_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  { reference: '{{title}}', type: 'String', description: 'The display title (e.g. "Protect your order")' },
  { reference: '{{subtitle}}', type: 'String', description: 'The short line under the title' },
  { reference: '{{pricingMethod}}', type: 'String', description: "'fixed' or 'percent'" },
  { reference: '{{selectingMethodType}}', type: 'String', description: "'toggle', 'checkbox' or 'quick_checkout'" },
  { reference: '{{infoType}}', type: 'String', description: "'tooltip' or 'modal'" },
  {
    reference: 'data-pdq-slot="control"',
    type: 'Slot',
    description: 'The accept/decline control — click handling + Shopify are ours. Required.',
  },
  { reference: 'data-pdq-slot="price"', type: 'Slot', description: 'The live price (re-prices with the cart)' },
];

// Quick-checkout button icon options — the DS "Smart cart Icons" set, served from the shop-plugin CDN by
// name (same pattern as the Goals/Timer widgets: `${cdn}/icons/{name}-color.svg`). 'coupon' is the tag icon.
const iconCdn = resolveShopPluginCdn();
export const ORDER_PROTECTION_ICON_NAMES = ['coupon', 'diamond', 'shield', 'truck', 'gift'] as const;
export const ORDER_PROTECTION_ICON_URLS: string[] = ORDER_PROTECTION_ICON_NAMES.map(
  (name) => `${iconCdn}/icons/${name}-color.svg`,
);
export const ORDER_PROTECTION_ICON_LABELS = ['Tag', 'Diamond', 'Shield', 'Truck', 'Gift'];

// Modal Body list-item icon options — the same CDN pattern (Dot / Checkmark), plus "No icon" and custom
// upload provided by the iconpicker's popover.
export const ORDER_PROTECTION_LIST_ICON_NAMES = ['dot', 'checkmark'] as const;
export const ORDER_PROTECTION_LIST_ICON_URLS: string[] = ORDER_PROTECTION_LIST_ICON_NAMES.map(
  (name) => `${iconCdn}/icons/${name}-color.svg`,
);
export const ORDER_PROTECTION_LIST_ICON_LABELS = ['Dot', 'Checkmark'];

const DEFAULTS = {
  pricingMethod: 'fixed' as OrderProtectionPricingType,
  flatPrice: 0.98,
  percent: { minPrice: 0.1, maxPrice: 100, cartPercentage: 2 },
  title: 'Protect your order',
  subtitle: 'Coverage against loss, theft, or damage in transit.',
  // Blank = inherit the theme's secondary text colour.
  subtitleColor: '',
  // Defaults reproduce the card's existing hairline, so switching Border on doesn't jump the design.
  border: { enabled: false, color: '#E1E3E5', width: 1, radius: 8 },
  productImageBase64: '',
  selectingMethod: {
    type: 'quick_checkout' as OrderProtectionSelectingMethod,
    toggle: { defaultColor: '#FFFFFF', selectedColor: '#FFFFFF' },
    checkbox: { defaultColor: '#FFFFFF', selectedColor: '#FFFFFF' },
    autoSelected: true,
  },
  quickCheckout: {
    buttonLabel: 'Checkout →',
    loaderLabel: 'Checking Out...',
    routing: 'automatic' as OrderProtectionRoutingMode,
    customUrl: '',
    icon: ORDER_PROTECTION_ICON_URLS[4], // gift, matching the Figma default selection
    customIcon: '',
    backgroundColor: '#2347C5',
    textColor: '#FFFFFF',
    hoverColor: '#1B379E',
    iconColor: '#FFFFFF',
    cornerRadius: 4,
    continueWithout: {
      content: 'Continue without package protection',
      textColor: '#454572',
      hoverColor: '#2347C5',
    },
  },
  info: {
    type: 'tooltip' as OrderProtectionInfoType,
    tooltipContent: '',
    modalHeadline: '',
    modalBody: '',
    footer: '',
  },
};

export function createOrderProtectionListItem(value?: {
  icon?: string;
  customIcon?: string;
  title?: string;
  description?: string;
}): FormGroup<OrderProtectionListItemFormShape> {
  return new FormGroup<OrderProtectionListItemFormShape>({
    icon: new FormControl(value?.icon ?? ORDER_PROTECTION_LIST_ICON_URLS[0], { nonNullable: true }),
    customIcon: new FormControl(value?.customIcon ?? '', { nonNullable: true }),
    title: new FormControl(value?.title ?? '', { nonNullable: true }),
    description: new FormControl(value?.description ?? '', { nonNullable: true }),
  });
}

export function createOrderProtectionFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<OrderProtectionFormShape> {
  const formGroup = new FormGroup<OrderProtectionFormShape>({
    pricingMethod: new FormControl(DEFAULTS.pricingMethod, { nonNullable: true }),
    flatPrice: new FormControl(DEFAULTS.flatPrice, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(0)],
    }),
    percent: new FormGroup<PercentFormShape>({
      minPrice: new FormControl(DEFAULTS.percent.minPrice, { nonNullable: true }),
      maxPrice: new FormControl(DEFAULTS.percent.maxPrice, { nonNullable: true }),
      cartPercentage: new FormControl(DEFAULTS.percent.cartPercentage, {
        nonNullable: true,
        // Mirror the editor's pdq-input-arrows clamp ([min]="1" [max]="100"); catches loaded out-of-range values.
        validators: [Validators.required, Validators.min(1), Validators.max(100)],
      }),
    }),
    title: new FormControl(DEFAULTS.title, { nonNullable: true, validators: [nonBlankValidator()] }),
    subtitle: new FormControl(DEFAULTS.subtitle, { nonNullable: true }),
    subtitleColor: new FormControl(DEFAULTS.subtitleColor, { nonNullable: true }),
    border: new FormGroup<OrderProtectionBorderFormShape>({
      enabled: new FormControl(DEFAULTS.border.enabled, { nonNullable: true }),
      color: new FormControl(DEFAULTS.border.color, { nonNullable: true }),
      width: new FormControl(DEFAULTS.border.width, { nonNullable: true }),
      radius: new FormControl(DEFAULTS.border.radius, { nonNullable: true }),
    }),
    productImageBase64: new FormControl(DEFAULTS.productImageBase64, { nonNullable: true }),
    selectingMethod: new FormGroup<OrderProtectionSelectingMethodFormShape>({
      type: new FormControl(DEFAULTS.selectingMethod.type, { nonNullable: true }),
      toggle: new FormGroup<OrderProtectionSelectorColorsFormShape>({
        defaultColor: new FormControl(DEFAULTS.selectingMethod.toggle.defaultColor, { nonNullable: true }),
        selectedColor: new FormControl(DEFAULTS.selectingMethod.toggle.selectedColor, { nonNullable: true }),
      }),
      checkbox: new FormGroup<OrderProtectionSelectorColorsFormShape>({
        defaultColor: new FormControl(DEFAULTS.selectingMethod.checkbox.defaultColor, { nonNullable: true }),
        selectedColor: new FormControl(DEFAULTS.selectingMethod.checkbox.selectedColor, { nonNullable: true }),
      }),
      autoSelected: new FormControl(DEFAULTS.selectingMethod.autoSelected, { nonNullable: true }),
    }),
    quickCheckout: new FormGroup<OrderProtectionQuickCheckoutFormShape>({
      buttonLabel: new FormControl(DEFAULTS.quickCheckout.buttonLabel, { nonNullable: true }),
      loaderLabel: new FormControl(DEFAULTS.quickCheckout.loaderLabel, { nonNullable: true }),
      routing: new FormControl(DEFAULTS.quickCheckout.routing, { nonNullable: true }),
      customUrl: new FormControl(DEFAULTS.quickCheckout.customUrl, { nonNullable: true }),
      icon: new FormControl(DEFAULTS.quickCheckout.icon, { nonNullable: true }),
      customIcon: new FormControl(DEFAULTS.quickCheckout.customIcon, { nonNullable: true }),
      backgroundColor: new FormControl(DEFAULTS.quickCheckout.backgroundColor, { nonNullable: true }),
      textColor: new FormControl(DEFAULTS.quickCheckout.textColor, { nonNullable: true }),
      hoverColor: new FormControl(DEFAULTS.quickCheckout.hoverColor, { nonNullable: true }),
      iconColor: new FormControl(DEFAULTS.quickCheckout.iconColor, { nonNullable: true }),
      cornerRadius: new FormControl(DEFAULTS.quickCheckout.cornerRadius, { nonNullable: true }),
      continueWithout: new FormGroup<OrderProtectionContinueWithoutFormShape>({
        content: new FormControl(DEFAULTS.quickCheckout.continueWithout.content, { nonNullable: true }),
        textColor: new FormControl(DEFAULTS.quickCheckout.continueWithout.textColor, { nonNullable: true }),
        hoverColor: new FormControl(DEFAULTS.quickCheckout.continueWithout.hoverColor, { nonNullable: true }),
      }),
    }),
    info: new FormGroup<OrderProtectionInfoFormShape>({
      type: new FormControl(DEFAULTS.info.type, { nonNullable: true }),
      tooltipContent: new FormControl(DEFAULTS.info.tooltipContent, { nonNullable: true }),
      modalHeadline: new FormControl(DEFAULTS.info.modalHeadline, { nonNullable: true }),
      modalBody: new FormControl(DEFAULTS.info.modalBody, { nonNullable: true }),
      listItems: new FormArray<FormGroup<OrderProtectionListItemFormShape>>([]),
      footer: new FormControl(DEFAULTS.info.footer, { nonNullable: true }),
      sectionOrder: new FormControl<OrderProtectionModalSection[]>([...ORDER_PROTECTION_MODAL_SECTIONS], {
        nonNullable: true,
      }),
    }),
    customCode: createCustomCodeFormGroup(undefined, ORDER_PROTECTION_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings, { emitEvent: false });
    // FormArray isn't populated by patchValue — rebuild the list items explicitly.
    const items = (
      initialSettings['info'] as {
        listItems?: { icon?: string; customIcon?: string; title?: string; description?: string }[];
      }
    )?.listItems;
    if (Array.isArray(items)) {
      const array = formGroup.controls.info.controls.listItems;
      array.clear();
      items.forEach((item) => array.push(createOrderProtectionListItem(item)));
    }
  }

  return formGroup;
}
